/**
 * Array layout engine (design tool v2 — plan-based projects). Pure and deterministic; all values in mm on the ROOF
 * PLANE (callers convert plan drawings with `planProjection`).
 *
 *  - Per-edge setbacks (eave, ridge, hip, rake, valley, parapet… — fire / maintenance clearances are explicit inputs).
 *  - Exclusions: closed polygons with a clearance (obstacles, equipment service zones, hazard zones) and open polylines
 *    with a half width (maintenance walkways, lifeline corridors) — panels keep out of both.
 *  - Maintenance aisles: an extra gap after every n rows / m columns.
 *  - Grid phase: automatic (best of a deterministic set of offsets) or fixed by the user (offset stays put while the
 *    roof is edited, so manually removed slots keep their identity).
 *  - Slot ids `r<row>c<col>` are absolute grid indices from the roof-plane origin (stable under translation of the
 *    outline for a fixed phase).
 * Geometric only: it says nothing about structural adequacy, wind zones or the applicable fire code.
 */
import {
  bounds,
  pointInPolygon,
  pointSegmentDistance,
  polygonContains,
  rotate,
  segmentsIntersect,
  type Vec,
} from './geometry';

export const EDGE_KINDS = ['EAVE', 'RIDGE', 'HIP', 'RAKE', 'VALLEY', 'PARAPET', 'OTHER'] as const;
export type EdgeKind = (typeof EDGE_KINDS)[number];
export const EDGE_KIND_LABEL: Record<EdgeKind, string> = {
  EAVE: 'Mép mái (máng xối)',
  RIDGE: 'Đỉnh mái',
  HIP: 'Sống mái (mái 4 dốc)',
  RAKE: 'Mép đầu hồi',
  VALLEY: 'Khe mái',
  PARAPET: 'Tường chắn mái',
  OTHER: 'Cạnh khác',
};

export interface ArrayExclusion {
  id: string;
  kind: string;
  points: Vec[];
  /** true = polygon area; false = polyline (walkway, corridor) */
  closed: boolean;
  /** Extra distance panels must keep from the shape */
  clearanceMm: number;
  /** Polylines: half of the corridor width */
  halfWidthMm?: number;
}

export interface ArrayLayoutInput {
  outline: Vec[];
  /** Setback for edge i (outline[i] → outline[i+1]); missing ⇒ 0 */
  edgeSetbacksMm: number[];
  exclusions: ArrayExclusion[];
  /** Row direction (deg, roof-plane coordinates) */
  angleDeg: number;
  module: { widthMm: number; lengthMm: number };
  orientation: 'PORTRAIT' | 'LANDSCAPE' | 'AUTO';
  colGapMm: number;
  rowGapMm: number;
  aisles?: { everyRows: number; rowAisleMm: number; everyCols: number; colAisleMm: number };
  /** Grid phase (mm, in the row frame). null ⇒ automatic. */
  offset?: { u: number; v: number } | null;
  /** Slots removed by the user */
  excluded?: string[];
  /**
   * Second outline the panels must also stay inside, keeping its own edge setbacks (installation zone: `outline` is
   * the zone, `container` the roof).
   */
  container?: { outline: Vec[]; edgeSetbacksMm: number[] };
  /** Automatic phase: count aisles from the first panel row / column, so the first array is a full block */
  anchorAisles?: boolean;
}

export interface ArrayPanel {
  id: string;
  row: number;
  col: number;
  /** TL, TR, BR, BL in roof-plane mm (TL→TR runs along the row) */
  corners: Vec[];
  center: Vec;
}

export interface ArrayRowSegment {
  row: number;
  fromCol: number;
  toCol: number;
  panels: number;
  /** Panel-to-panel length along the row, mm */
  lengthMm: number;
}

export type ArrayRejectReason = 'OUTSIDE' | 'SETBACK' | 'EXCLUSION';

export interface ArrayLayoutResult {
  panels: ArrayPanel[];
  /** Valid slots the user removed (shown as ghosts, can be restored) */
  removed: ArrayPanel[];
  orientation: 'PORTRAIT' | 'LANDSCAPE';
  offset: { u: number; v: number };
  /** Footprint along / across the row (mm) */
  along: number;
  across: number;
  rows: ArrayRowSegment[];
  rejected: Record<ArrayRejectReason, number>;
  /** Exclusion id → number of slots it removed */
  rejectedBy: Record<string, number>;
  tooLarge: boolean;
}

const EPS = 1e-6;
const TOUCH = 0.01;
const MAX_CELLS = 300_000;
const PHASES = [0, 0.25, 0.5, 0.75];

function segRectDistance(a: Vec, b: Vec, rect: Vec[]): number {
  let d = Infinity;
  for (let i = 0; i < 4; i++) {
    const p = rect[i]!;
    const q = rect[(i + 1) % 4]!;
    if (segmentsIntersect(a, b, p, q)) return 0;
    d = Math.min(
      d,
      pointSegmentDistance(p, a, b),
      pointSegmentDistance(a, p, q),
      pointSegmentDistance(b, p, q),
    );
  }
  return d;
}

function polyRectDistance(poly: Vec[], closed: boolean, rect: Vec[]): number {
  let d = Infinity;
  const n = closed ? poly.length : poly.length - 1;
  for (let i = 0; i < n; i++) {
    d = Math.min(d, segRectDistance(poly[i]!, poly[(i + 1) % poly.length]!, rect));
    if (d === 0) return 0;
  }
  if (closed && (pointInPolygon(rect[0]!, poly) || pointInPolygon(poly[0]!, rect))) return 0;
  return d;
}

function isConvex(poly: Vec[]): boolean {
  let sign = 0;
  for (let i = 0; i < poly.length; i++) {
    const a = poly[i]!;
    const b = poly[(i + 1) % poly.length]!;
    const c = poly[(i + 2) % poly.length]!;
    const z = (b.x - a.x) * (c.y - b.y) - (b.y - a.y) * (c.x - b.x);
    if (Math.abs(z) < 1e-9) continue;
    if (sign === 0) sign = Math.sign(z);
    else if (Math.sign(z) !== sign) return false;
  }
  return true;
}

interface Prepared {
  ex: ArrayExclusion;
  reach: number;
  box: { minX: number; minY: number; maxX: number; maxY: number };
}

/** Position of grid line i with an extra aisle after every `every` lines. */
const linePos = (i: number, step: number, every: number, aisle: number) =>
  i * step + (every > 0 ? Math.floor(i / every) * aisle : 0);

interface Bound {
  outline: Vec[];
  edges: (readonly [Vec, Vec])[];
  setbacks: number[];
  convex: boolean;
}

function boundOf(outline: Vec[], setbacksMm: number[]): Bound {
  return {
    outline,
    edges: outline.map((p, i) => [p, outline[(i + 1) % outline.length]!] as const),
    setbacks: outline.map((_, i) => Math.max(0, setbacksMm[i] ?? 0)),
    convex: isConvex(outline),
  };
}

/** Why a panel rectangle does not fit an outline (containment with a 0.01 mm inset, then edge setbacks). */
function misfit(bd: Bound, rect: Vec[], inset: Vec[]): 'OUTSIDE' | 'SETBACK' | null {
  if (bd.convex ? !inset.every((q) => pointInPolygon(q, bd.outline)) : !polygonContains(bd.outline, inset)) return 'OUTSIDE';
  // Inside a convex outline the nearest point of the rectangle to an edge is one of its corners
  for (let i = 0; i < bd.edges.length; i++) {
    if (!(bd.setbacks[i]! > 0)) continue;
    const [ea, eb] = bd.edges[i]!;
    const d = bd.convex ? Math.min(...rect.map((q) => pointSegmentDistance(q, ea, eb))) : segRectDistance(ea, eb, rect);
    if (d < bd.setbacks[i]! - EPS) return 'SETBACK';
  }
  return null;
}

function layoutOnce(
  input: ArrayLayoutInput,
  orientation: 'PORTRAIT' | 'LANDSCAPE',
  offset: { u: number; v: number },
  prepared: Prepared[],
  collect: boolean,
  fits: Bound[],
): ArrayLayoutResult & { count: number } {
  const { widthMm, lengthMm } = input.module;
  const along = orientation === 'PORTRAIT' ? widthMm : lengthMm;
  const across = orientation === 'PORTRAIT' ? lengthMm : widthMm;
  const stepU = along + Math.max(0, input.colGapMm);
  const stepV = across + Math.max(0, input.rowGapMm);
  const a = input.aisles ?? { everyRows: 0, rowAisleMm: 0, everyCols: 0, colAisleMm: 0 };
  const eR = Math.max(0, Math.floor(a.everyRows));
  const eC = Math.max(0, Math.floor(a.everyCols));
  const local = input.outline.map((v) => rotate(v, -input.angleDeg));
  const b = bounds(local);
  const excluded = new Set(input.excluded ?? []);
  const res: ArrayLayoutResult & { count: number } = {
    count: 0,
    panels: [],
    removed: [],
    orientation,
    offset,
    along,
    across,
    rows: [],
    rejected: { OUTSIDE: 0, SETBACK: 0, EXCLUSION: 0 },
    rejectedBy: {},
    tooLarge: false,
  };
  if (!(along > 0 && across > 0) || local.length < 3) return res;
  const avgU = stepU + (eC ? a.colAisleMm / eC : 0);
  const avgV = stepV + (eR ? a.rowAisleMm / eR : 0);
  const c0 = Math.floor((b.minX - offset.u) / avgU) - 2;
  const c1 = Math.ceil((b.maxX - offset.u) / stepU) + 2;
  const r0 = Math.floor((b.minY - offset.v) / avgV) - 2;
  const r1 = Math.ceil((b.maxY - offset.v) / stepV) + 2;
  if ((c1 - c0) * (r1 - r0) > MAX_CELLS) {
    res.tooLarge = true;
    return res;
  }
  const back = (v: Vec) => rotate(v, input.angleDeg);
  for (let r = r0; r <= r1; r++) {
    const v = offset.v + linePos(r, stepV, eR, a.rowAisleMm);
    if (v < b.minY - EPS || v + across > b.maxY + EPS) continue;
    for (let c = c0; c <= c1; c++) {
      const u = offset.u + linePos(c, stepU, eC, a.colAisleMm);
      if (u < b.minX - EPS || u + along > b.maxX + EPS) continue;
      const rect = [
        { x: u, y: v },
        { x: u + along, y: v },
        { x: u + along, y: v + across },
        { x: u, y: v + across },
      ].map(back);
      // Containment with a 0.01 mm inset: a panel may touch an edge that has no setback
      const inset = [
        { x: u + TOUCH, y: v + TOUCH },
        { x: u + along - TOUCH, y: v + TOUCH },
        { x: u + along - TOUCH, y: v + across - TOUCH },
        { x: u + TOUCH, y: v + across - TOUCH },
      ].map(back);
      let why: 'OUTSIDE' | 'SETBACK' | null = null;
      for (const bd of fits) if ((why = misfit(bd, rect, inset))) break;
      if (why) {
        res.rejected[why]++;
        continue;
      }
      let bad = false;
      const rb = bounds(rect);
      for (const p of prepared) {
        if (
          rb.maxX < p.box.minX - p.reach ||
          rb.minX > p.box.maxX + p.reach ||
          rb.maxY < p.box.minY - p.reach ||
          rb.minY > p.box.maxY + p.reach
        )
          continue;
        const d = polyRectDistance(p.ex.points, p.ex.closed, rect);
        if (d === 0 || d < p.reach - EPS) {
          bad = true;
          if (collect) res.rejectedBy[p.ex.id] = (res.rejectedBy[p.ex.id] ?? 0) + 1;
          break;
        }
      }
      if (bad) {
        res.rejected.EXCLUSION++;
        continue;
      }
      res.count++;
      if (!collect) continue;
      const id = `r${r}c${c}`;
      const center = back({ x: u + along / 2, y: v + across / 2 });
      const panel: ArrayPanel = { id, row: r, col: c, corners: rect, center };
      if (excluded.has(id)) res.removed.push(panel);
      else res.panels.push(panel);
    }
  }
  if (!collect) return res;
  // Continuous row segments (a maintenance aisle or a missing slot splits a segment)
  const byRow = new Map<number, number[]>();
  for (const p of res.panels) byRow.set(p.row, [...(byRow.get(p.row) ?? []), p.col]);
  for (const [row, cols] of [...byRow].sort((x, y) => x[0] - y[0])) {
    cols.sort((x, y) => x - y);
    let start = cols[0]!;
    let prev = start;
    const flush = (end: number) => {
      const n = end - start + 1;
      res.rows.push({
        row,
        fromCol: start,
        toCol: end,
        panels: n,
        lengthMm: n * along + (n - 1) * Math.max(0, input.colGapMm),
      });
    };
    for (const c of cols.slice(1)) {
      const aisleBetween = eC > 0 && Math.floor(c / eC) !== Math.floor(prev / eC);
      if (c !== prev + 1 || aisleBetween) {
        flush(prev);
        start = c;
      }
      prev = c;
    }
    flush(prev);
  }
  return res;
}

/** Lay out one roof. AUTO orientation keeps the orientation with more panels (portrait on ties). */
export function layoutArray(input: ArrayLayoutInput): ArrayLayoutResult {
  const prepared: Prepared[] = input.exclusions
    .filter((e) => e.points.length >= (e.closed ? 3 : 2))
    .map((ex) => ({
      ex,
      reach: Math.max(0, ex.clearanceMm) + (ex.closed ? 0 : Math.max(0, ex.halfWidthMm ?? 0)),
      box: bounds(ex.points),
    }));
  const orientations: ('PORTRAIT' | 'LANDSCAPE')[] =
    input.orientation === 'AUTO' ? ['PORTRAIT', 'LANDSCAPE'] : [input.orientation];
  const fits = [boundOf(input.outline, input.edgeSetbacksMm)];
  if (input.container) fits.push(boundOf(input.container.outline, input.container.edgeSetbacksMm));
  const a = input.aisles ?? { everyRows: 0, rowAisleMm: 0, everyCols: 0, colAisleMm: 0 };
  const eR = Math.max(0, Math.floor(a.everyRows));
  const eC = Math.max(0, Math.floor(a.everyCols));
  const anchor = !input.offset && !!input.anchorAisles && (eR > 0 || eC > 0);
  let best: (ArrayLayoutResult & { count: number }) | null = null;
  for (const o of orientations) {
    const along = o === 'PORTRAIT' ? input.module.widthMm : input.module.lengthMm;
    const across = o === 'PORTRAIT' ? input.module.lengthMm : input.module.widthMm;
    const stepU = along + Math.max(0, input.colGapMm);
    const stepV = across + Math.max(0, input.rowGapMm);
    let candidates: { u: number; v: number }[];
    if (input.offset) candidates = [input.offset];
    else {
      // Grid anchored at the outline's minimum corner and at the inner edge of each setback band (row frame), then
      // shifted by fractions of a step; duplicates (same phase modulo the step) are skipped. With a container its
      // minimum corner and setback bands are anchors too.
      const bases = [{ outline: input.outline, setbacks: input.edgeSetbacksMm }];
      if (input.container) bases.push({ outline: input.container.outline, setbacks: input.container.edgeSetbacksMm });
      const boxes = bases.map((base) => bounds(base.outline.map((v) => rotate(v, -input.angleDeg))));
      const axis = (pick: (b: (typeof boxes)[number]) => number, step: number) => {
        const ref = pick(boxes[0]!);
        const seen = new Set<number>();
        const out: number[] = [];
        bases.forEach((base, i) => {
          for (const s0 of new Set([0, ...base.setbacks.filter((x) => x > 0)]))
            for (const f of PHASES) {
              const x = pick(boxes[i]!) + s0 + f * step;
              const key = Math.round((((x - ref) % step) + step) % step);
              if (seen.has(key)) continue;
              seen.add(key);
              out.push(x);
            }
        });
        return out;
      };
      const us = axis((b) => b.minX, stepU);
      const vs = axis((b) => b.minY, stepV);
      candidates = [];
      for (const v of vs) for (const u of us) candidates.push({ u, v });
    }
    for (let off of candidates) {
      if (anchor) {
        // Shift the phase by whole grid lines so the first panel row / column is line 0 of the first array
        const first = layoutOnce(input, o, off, prepared, true, fits);
        const all = [...first.panels, ...first.removed];
        if (all.length) {
          const r0 = Math.min(...all.map((x) => x.row));
          const c0 = Math.min(...all.map((x) => x.col));
          off = { u: off.u + linePos(c0, stepU, eC, a.colAisleMm), v: off.v + linePos(r0, stepV, eR, a.rowAisleMm) };
        }
      }
      const r = layoutOnce(input, o, off, prepared, false, fits);
      if (!best || r.count > best.count) best = r;
    }
  }
  // Recompute the winner with slot ids, rows and per-exclusion statistics
  const { count: _count, ...result } = layoutOnce(input, best!.orientation, best!.offset, prepared, true, fits);
  return result;
}

/** Corners of a rectangle with the given half-sizes around a polyline segment (walkway strip) — for drawing / area. */
export function stripPolygon(a: Vec, b: Vec, halfWidth: number): Vec[] {
  const dx = b.x - a.x;
  const dy = b.y - a.y;
  const l = Math.hypot(dx, dy) || 1;
  const nx = (-dy / l) * halfWidth;
  const ny = (dx / l) * halfWidth;
  return [
    { x: a.x + nx, y: a.y + ny },
    { x: b.x + nx, y: b.y + ny },
    { x: b.x - nx, y: b.y - ny },
    { x: a.x - nx, y: a.y - ny },
  ];
}
